import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Stack, ThemeProvider } from '@mui/material';

import { passengerThemeV2 } from '@mopla/mui';
import {
	DTicketPageLayout,
	MplOperatorWordmark,
	MplTabPanel,
	MplTabs,
	MplTicketCard,
} from '@mopla/ui';

import { usePassengerAppNavigate } from '../../../hooks';
import { useOperators } from '../ticketshop/hooks';

import {
	EmptyState,
	PriceGroupLegalNotes,
	PriceGroupSubheader,
	TariffConditionsLink,
} from './components';
import { useOperatorTickets } from './hooks';
import { OperatorTicketsLoading } from './OperatorTicketsLoading';
import { ITicketCard } from './types';
import { buildTicketSubtitle } from './utils';

export const OperatorTickets = () => {
	const { t } = useTranslation('ticketshop');
	const { operatorId } = useParams<{ operatorId: string }>();
	const location = useLocation();
	const { navigateBack, navigateToTicketshop, navigateToOperatorTicketDetail } =
		usePassengerAppNavigate();
	// Replace on a deep link, so Ticketshop's own Back doesn't pop back here.
	const handleBack = useCallback(
		() => navigateBack(() => navigateToTicketshop({ replace: true })),
		[navigateBack, navigateToTicketshop]
	);

	// Header title comes from router state when navigating from Ticketshop
	// (instant, no shift). Falls back to the operators fetch for direct URL hits.
	const navStateOperatorName = (
		location.state as { operatorName?: string } | null
	)?.operatorName;
	const { operators, isLoading: isOperatorsLoading } = useOperators();
	const operator = useMemo(
		() => operators.find((o) => o.id === operatorId),
		[operators, operatorId]
	);
	const headerTitle = navStateOperatorName ?? operator?.name ?? '';
	const isTitleLoading = !headerTitle && isOperatorsLoading;

	const { operatorTickets, isLoading } = useOperatorTickets(operatorId);

	const [activeTabValue, setActiveTabValue] = useState<string | number>('');

	// Keep the active tab in sync with available price groups.
	const tabs = useMemo(
		() =>
			operatorTickets?.priceGroups.map((pg) => ({
				value: pg.id,
				label: pg.name,
			})) ?? [],
		[operatorTickets]
	);

	// Default to the first tab once data arrives (or when groups change).
	const effectiveTabValue =
		tabs.find((tab) => tab.value === activeTabValue)?.value ??
		tabs[0]?.value ??
		'';

	const activePriceGroup = useMemo(
		() =>
			operatorTickets?.priceGroups.find((pg) => pg.id === effectiveTabValue),
		[operatorTickets, effectiveTabValue]
	);

	const handleTicketClick = useCallback(
		(ticket: ITicketCard) => {
			if (!operatorId) return;

			// Per docs §6 both zone-based (Step 2a) and stop-based (Step 2b) route
			// to the same detail endpoint; the page branches on pricingModel.
			navigateToOperatorTicketDetail(operatorId, ticket.id, {
				operatorName: headerTitle || undefined,
				tariffConditionsUrl: operatorTickets?.tariffConditionsUrl,
				// Carry the selected price group so the detail + checkout price the
				// ticket against it (MC-11606), not always the first group.
				priceGroupId: effectiveTabValue ? String(effectiveTabValue) : undefined,
			});
		},
		[
			navigateToOperatorTicketDetail,
			operatorId,
			headerTitle,
			operatorTickets?.tariffConditionsUrl,
			effectiveTabValue,
		]
	);

	if (isLoading) {
		return <OperatorTicketsLoading onBack={handleBack} title={headerTitle} />;
	}

	const hasPriceGroups = (operatorTickets?.priceGroups.length ?? 0) > 0;

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<DTicketPageLayout
				title={headerTitle}
				onBack={handleBack}
				scrollable
				dataTestId="operator-tickets-page"
				isTitleLoading={isTitleLoading}
			>
				{/* Operator wordmark — always shown above the tabs, independent of
				    the active price-group tab. 8px gap to the tabs (Figma). */}
				<Box sx={{ mb: 1 }}>
					<MplOperatorWordmark
						src={operator?.wordmarkUrl}
						dataTestId="operator-tickets-banner"
					/>
				</Box>
				{!hasPriceGroups ? (
					<EmptyState />
				) : (
					<Stack gap={2}>
						<MplTabs
							tabs={tabs}
							value={effectiveTabValue}
							onChange={setActiveTabValue}
							scrollable={false}
							indicatorColor={passengerThemeV2.palette.primary.main}
							dividerHeight={2}
							dataTestId="operator-tickets-tabs"
						/>
						{operatorTickets?.priceGroups.map((pg) => (
							<MplTabPanel
								key={pg.id}
								value={pg.id}
								activeValue={effectiveTabValue}
							>
								<Stack gap={1}>
									<PriceGroupSubheader
										text={t('operator_tickets.subheader', {
											displayCode: pg.displayCode,
										})}
									/>
									{pg.tickets.length === 0 ? (
										<EmptyState />
									) : (
										<Stack gap={1}>
											{pg.tickets.map((ticket) => (
												<MplTicketCard
													key={ticket.id}
													title={ticket.name}
													subtitle={buildTicketSubtitle(ticket, t)}
													onClick={() => handleTicketClick(ticket)}
													dataTestId={`ticket-card-${ticket.id}`}
												/>
											))}
										</Stack>
									)}
									{(pg.legalText || pg.conditionsUrl) && (
										<PriceGroupLegalNotes
											conditionsUrl={pg.conditionsUrl}
											legalText={pg.legalText}
										/>
									)}
								</Stack>
							</MplTabPanel>
						))}
						{activePriceGroup && operatorTickets?.tariffConditionsUrl && (
							<TariffConditionsLink
								href={operatorTickets.tariffConditionsUrl}
								label={t('operator_tickets.tariff_conditions_link')}
							/>
						)}
					</Stack>
				)}
			</DTicketPageLayout>
		</ThemeProvider>
	);
};
